// Keeps an open patient record in step with changes other users make to it, off the back of the
// PushEventService poll: progress notes, vision, medications, allergies, diagnoses, uploaded files
// and generated documents.
//
// Every one of these reloads rather than patches, and for the same underlying reason: what the
// record shows is not the stored row. The notes list is four sources folded together and filtered
// per note by the viewer's security level; the vision table is the most recent value of each
// measurement across every record, worked out server-side; medications and diagnoses are split
// into per-eye rows on the client; files carry per-viewer permissions and preview state. None of
// the endpoints returns one record in the shape the view holds it, so the view's own fetch is the
// only refresh that gets it right. What this service adds is the deciding and the batching - see
// LiveReloadService - so an event for another patient costs nothing and a burst costs one request.
//
// Events carry only ids. Nothing clinical is on the wire, and nothing from an event is drawn: the
// re-fetch goes through the same endpoint, and the same filtering, as the first load did.
//
// A live refresh has to be invisible. It is something happening to a screen the user did not touch,
// so the section is re-fetched in the background - no spinner, no page reset, no re-render of
// anything but the rows that changed. Subscribers get the reload asked for with a background flag
// for that reason; the same fetch run after the user's own save keeps its spinner.
//
// A view subscribes per section, with its own scope, so the listeners die with the view:
//
//   PatientRecordLiveUpdateService.subscribeMedications($scope, {
//       patientId: function () { return $scope.CP.patient && $scope.CP.patient.id; },
//       reload: function () { fetchPatientsMedication(); },
//   });
app.service("PatientRecordLiveUpdateService", [
	"LiveReloadService",
	"PUSH_EVENTS",
	function (LiveReloadService, PUSH_EVENTS) {
		/**
		 * Wires a view up to one section's push events for the life of its scope.
		 *
		 * @param events the PUSH_EVENTS names that change this section
		 * @param scope  the view's scope; listeners are removed when it is destroyed
		 * @param spec   patientId() - the patient whose record is open; falsy when none is, which
		 *                   drops the event without a request
		 *               reload() - the view's own fetch for this section
		 */
		function subscribeTo(events, scope, spec) {
			LiveReloadService.subscribe(scope, {
				events: events,
				isRelevant: function (payload) {
					const patientId = spec.patientId();
					return !!patientId && LiveReloadService.sameId(patientId, payload.patientId);
				},
				reload: spec.reload,
			});
		}

		const service = {
			/**
			 * The patient's own details, edited by another user: name, contacts, address, identifiers,
			 * insurance, next of kin.
			 *
			 * Unlike every other subscription here this one does not reload a section - it hands the
			 * event to the view, because what to do about it depends on what the user is in the middle
			 * of. A record being read can take the new details without saying anything; a record being
			 * edited cannot, since replacing the fields under someone mid-edit throws away their typing
			 * without asking.
			 *
			 * Your own save arrives here too, on the same practice channel. It is deliberately not
			 * filtered out: re-reading a record to match what is saved is right whoever saved it, and
			 * the same user can have the record open in more than one place. Who made the change
			 * decides how it is announced, not whether it is applied, so the payload carries the
			 * editor's id and the view decides - see the dashboard's _refreshPatientDetailsFromServer().
			 *
			 * @param spec patientId() - the patient whose record is open; falsy when none is
			 *             onUpdated(payload) - the patient changed; payload carries updatedByUserId
			 */
			subscribePatientDetails: function (scope, spec) {
				LiveReloadService.subscribe(scope, {
					events: [PUSH_EVENTS.PATIENT_UPDATED],
					isRelevant: function (payload) {
						const patientId = spec.patientId();
						return !!patientId && LiveReloadService.sameId(patientId, payload.patientId);
					},
					handleLocally: function (payload) {
						spec.onUpdated(payload);
						return true;
					},
					reload: function () {},
				});
			},

			/**
			 * The open patient was transferred to another practice, and so out of this one.
			 *
			 * Handed to the view rather than reloaded, because there is nothing left to reload: the
			 * record has gone from this practice, and the only thing to do is say so and leave it. The
			 * event reaches this practice's channel only, so receiving it at all means the patient has
			 * gone from under this user specifically.
			 *
			 * @param spec patientId() - the patient whose record is open; falsy when none is
			 *             onTransferredOut(payload) - payload carries toPracticeName
			 */
			subscribePatientTransferredOut: function (scope, spec) {
				LiveReloadService.subscribe(scope, {
					events: [PUSH_EVENTS.PATIENT_TRANSFERRED_OUT],
					isRelevant: function (payload) {
						const patientId = spec.patientId();
						return !!patientId && LiveReloadService.sameId(patientId, payload.patientId);
					},
					handleLocally: function (payload) {
						spec.onTransferredOut(payload);
						return true;
					},
					reload: function () {},
				});
			},

			/** Progress notes: written, edited, signed off, deleted or restored. */
			subscribeProgressNotes: function (scope, spec) {
				subscribeTo([
					PUSH_EVENTS.PROGRESS_NOTE_ADDED,
					PUSH_EVENTS.PROGRESS_NOTE_UPDATED,
					PUSH_EVENTS.PROGRESS_NOTE_DELETED,
				], scope, spec);
			},

			/**
			 * SNOMED concepts written back onto a note or an AI conversation by the extraction Lambda,
			 * a few seconds after the record was saved. Handed to the view uncoalesced with its payload
			 * rather than reloaded, because the payload's sourceType decides which of the timeline's
			 * lists to re-read - notes or conversations - and re-reading both for every event would
			 * double the requests for nothing.
			 *
			 * @param spec patientId() - as elsewhere
			 *             onExtracted(payload) - payload carries sourceType and sourceId
			 */
			subscribeSnomedExtraction: function (scope, spec) {
				LiveReloadService.subscribe(scope, {
					events: [PUSH_EVENTS.PROGRESS_NOTE_SNOMED_EXTRACTED],
					isRelevant: function (payload) {
						const patientId = spec.patientId();
						return !!patientId && LiveReloadService.sameId(patientId, payload.patientId);
					},
					handleLocally: function (payload) {
						spec.onExtracted(payload);
						return true;
					},
					reload: function () {},
				});
			},

			/**
			 * Vision: documented or deleted. Documenting vision also files a PDF, so a subscriber will
			 * usually refresh its document list alongside the table - as the view does after its own
			 * save.
			 */
			subscribeVision: function (scope, spec) {
				subscribeTo([PUSH_EVENTS.VISION_ADDED, PUSH_EVENTS.VISION_DELETED], scope, spec);
			},

			/** Medications: added, edited, re-prescribed or deleted. */
			subscribeMedications: function (scope, spec) {
				subscribeTo([
					PUSH_EVENTS.MEDICATION_ADDED,
					PUSH_EVENTS.MEDICATION_UPDATED,
					PUSH_EVENTS.MEDICATION_DELETED,
				], scope, spec);
			},

			/** Allergies: added, verified, made active or inactive, or deleted. */
			subscribeAllergies: function (scope, spec) {
				subscribeTo([
					PUSH_EVENTS.ALLERGY_ADDED,
					PUSH_EVENTS.ALLERGY_UPDATED,
					PUSH_EVENTS.ALLERGY_DELETED,
				], scope, spec);
			},

			/**
			 * Diagnoses: added (from any pathway), verified, edited (e.g. date of onset), inactivated
			 * or rejected. An edit arrives as DIAGNOSIS_UPDATED with lastUpdatedByUserId in the payload.
			 */
			subscribeDiagnoses: function (scope, spec) {
				subscribeTo([
					PUSH_EVENTS.DIAGNOSIS_ADDED,
					PUSH_EVENTS.DIAGNOSIS_UPDATED,
					PUSH_EVENTS.DIAGNOSIS_DELETED,
				], scope, spec);
			},

			/** Uploaded files: uploaded, re-described or deleted. Not generated documents. */
			subscribePatientFiles: function (scope, spec) {
				subscribeTo([
					PUSH_EVENTS.PATIENT_FILE_ADDED,
					PUSH_EVENTS.PATIENT_FILE_UPDATED,
					PUSH_EVENTS.PATIENT_FILE_DELETED,
				], scope, spec);
			},

			/**
			 * The analyser finished with one uploaded file. Unlike the rest of this service this does
			 * not reload: the file table already knows how to re-read one file and replace its row, and
			 * the whole point of the event is that only that row changes. So the payload is handed to
			 * the view as it arrives, uncoalesced.
			 *
			 * @param spec patientId() - as elsewhere
			 *             onAnalysed(payload) - apply the one file; payload carries fileId
			 */
			subscribePatientFileAnalysed: function (scope, spec) {
				LiveReloadService.subscribe(scope, {
					events: [PUSH_EVENTS.PATIENT_FILE_ANALYSED],
					isRelevant: function (payload) {
						const patientId = spec.patientId();
						return !!patientId && !!payload.fileId && LiveReloadService.sameId(patientId, payload.patientId);
					},
					handleLocally: function (payload) {
						spec.onAnalysed(payload);
						return true;
					},
					reload: function () {},
				});
			},

			/**
			 * Referrals: created for the patient, whether by a user or by the analyser recognising an
			 * uploaded file as one. The claim widget's referral picker is drawn from this list.
			 */
			subscribeReferrals: function (scope, spec) {
				subscribeTo([PUSH_EVENTS.REFERRAL_ADDED], scope, spec);
			},

			/**
			 * Care team: a member added, activated or deactivated. Many of these happen without anyone
			 * touching the care team - uploading a referral adds its GP, saving a note adds its author -
			 * so the list on screen drifts unless it is told. One event covers every kind of change.
			 */
			subscribeCareTeam: function (scope, spec) {
				subscribeTo([PUSH_EVENTS.CARE_TEAM_UPDATED], scope, spec);
			},

			/**
			 * The timeline charts: intraocular pressure, refraction and acuity from vision, axial length,
			 * keratometry and astigmatism from biometry. A change to either source redraws the same
			 * component, so both are one subscription. Biometry is only ever added by the file analyser,
			 * which is why the added event comes from a Lambda and the deleted one from the API.
			 */
			subscribeClinicalCharts: function (scope, spec) {
				subscribeTo([
					PUSH_EVENTS.VISION_ADDED,
					PUSH_EVENTS.VISION_DELETED,
					PUSH_EVENTS.BIOMETRY_ADDED,
					PUSH_EVENTS.BIOMETRY_DELETED,
				], scope, spec);
			},

			/**
			 * Generated documents: notes, letters and forms rendered to PDF - added to the record, or
			 * regenerated in place. The deleted event is listened for but nothing publishes it yet.
			 */
			subscribeDocuments: function (scope, spec) {
				subscribeTo([
					PUSH_EVENTS.DOCUMENT_ADDED,
					PUSH_EVENTS.DOCUMENT_UPDATED,
					PUSH_EVENTS.DOCUMENT_DELETED,
				], scope, spec);
			},
		};

		return service;
	},
]);
